import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, TriangleAlert, RefreshCw, Download } from 'lucide-react';
import { financeService } from '../../services/financeService';
import { useAppContext } from '../../context/AppContext';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import { usePeriodDrilldown } from '../../hooks/usePeriodDrilldown';
import { exportPnl } from '../../utils/pnlExcel';

// =============================================================================
// Finance › Profit & Loss   (LIVE — depreciation pending)
//
// AN INCOME STATEMENT, NOT A LIST OF ACCOUNTS. Revenue, discounts off it, cost
// of sales and distribution off that, overheads off that — with the subtotals
// an accountant looks for first and reads before anything else. The previous
// version sorted every account by size into one flat grid, which is a perfectly
// good cost report and not a P&L.
//
// THE STRUCTURE IS DATA, NOT CODE. Each account carries
// md_gl_accounts.pnl_section, a fixed industry-neutral enum, and the section
// order, labels and subtotal positions arrive from the server with the numbers
// (utils/pnlStructure.js). So:
//   - a services client maps nothing to DISTRIBUTION and that section, and its
//     subtotal, simply are not drawn;
//   - nobody edits this file to onboard an industry;
//   - the server's grouping and the page's layout cannot drift apart.
//
// SIGN. Amounts arrive in presentation sign — income positive, cost negative —
// so every subtotal is the plain sum of the lines above it and the column can
// be added up by hand. The debit-positive ledger is flipped once, server-side.
//
// TWO THINGS ARE SHOWN BEFORE THE PROFIT, DELIBERATELY: cost that posts nowhere
// (absent from every figure), and budget sitting on balance-sheet accounts
// (excluded from the statement by definition). Both make the profit wrong in a
// way no total can reveal on its own.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';

// Accounting convention: negatives in brackets. An accountant reads a bracket
// faster than a minus sign, and this page is for accountants.
const fmtR = (n) => {
  const v = Math.round(Number(n) || 0);
  if (v === 0) return '—';
  const s = Math.abs(v).toLocaleString();
  return v < 0 ? `(${s})` : s;
};
const fmtPct = (n) => (Number.isFinite(n) && n !== 0 ? `${n.toFixed(1)}%` : '');

// Section header, detail line, subtotal, grand total — four weights, so the
// statement has a visible hierarchy on a canvas grid that has no borders.
const T_SECTION = { bgCell: 'rgba(100, 116, 139, 0.13)', baseFontStyle: '600 12px' };
const T_SUBTOTAL = { bgCell: 'rgba(37, 99, 235, 0.13)', baseFontStyle: '700 12px' };
const T_GRAND = { bgCell: 'rgba(16, 185, 129, 0.18)', baseFontStyle: '700 13px' };
const T_GRAND_NEG = { bgCell: 'rgba(239, 68, 68, 0.18)', baseFontStyle: '700 13px' };
const T_MUTED = { textDark: '#94a3b8' };
const T_YEAR = { bgCell: 'rgba(100, 116, 139, 0.10)' };

const GRAINS = [
  ['account', 'Accounts'],
  ['module', 'Modules'],
  ['site', 'Sites'],
];

const EMPTY = { structure: { sections: [], subtotals: [] }, rows: [], unclassified: [], unposted: [] };

const sumOf = (row, periods) => periods.reduce((a, p) => a + (row.values[p - 1] || 0), 0);

export default function ProfitAndLoss() {
  const navigate = useNavigate();
  const { selectedVersion, fyStartMonth } = useAppContext();

  const [grain, setGrain] = useState('account');
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState({}); // { COGS: true }

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await financeService.getPnl(grain);
      setData(res.data || EMPTY);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the income statement.');
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [grain]);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion]);

  // Memoised because everything below keys off identity: a fresh [] each render
  // would recompute the whole statement on every keystroke elsewhere.
  const sections = useMemo(() => data.structure?.sections || [], [data.structure]);
  const subtotals = useMemo(() => data.structure?.subtotals || [], [data.structure]);

  // ---- Pivot: one API row per key per period -> one line of twelve ----------
  const lines = useMemo(() => {
    const byKey = new Map();
    (data.rows || []).forEach((r) => {
      // Never drop a row for want of a key — an unkeyed line is money, and
      // money the statement silently loses is the one thing this page must not
      // do. The server buckets nulls; this is the belt to that pair of braces.
      const key = r.row_key || '(unassigned)';
      const k = `${r.pnl_section}|${key}`;
      if (!byKey.has(k)) {
        byKey.set(k, {
          key: k,
          section: r.pnl_section,
          label: r.row_label || key,
          code: key,
          group: r.account_group || '',
          fallback: !!r.is_fallback,
          values: Array(12).fill(0),
        });
      }
      byKey.get(k).values[r.financial_period - 1] += Number(r.amount) || 0;
    });
    return [...byKey.values()].map((l) => ({ ...l, total: l.values.reduce((a, v) => a + v, 0) }));
  }, [data.rows]);

  // Per-section period vectors, which every total on the page is built from.
  const bySection = useMemo(() => {
    const m = new Map();
    sections.forEach((s) => {
      const mine = lines
        .filter((l) => l.section === s.key)
        .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
      const values = Array(12).fill(0);
      mine.forEach((l) => l.values.forEach((v, i) => { values[i] += v; }));
      m.set(s.key, { lines: mine, values, total: values.reduce((a, v) => a + v, 0) });
    });
    return m;
  }, [sections, lines]);

  const statementKeys = useMemo(
    () => sections.filter((s) => !s.excluded).map((s) => s.key),
    [sections]
  );

  // A subtotal is the running sum from the top of the statement down to its
  // `through` section — the way a P&L actually reads. Deriving it from the
  // order means a new section is inside every subtotal below it automatically.
  const runningTo = useCallback((throughKey) => {
    const end = statementKeys.indexOf(throughKey);
    const values = Array(12).fill(0);
    if (end < 0) return { values, total: 0 };
    statementKeys.slice(0, end + 1).forEach((k) => {
      const sec = bySection.get(k);
      if (sec) sec.values.forEach((v, i) => { values[i] += v; });
    });
    return { values, total: values.reduce((a, v) => a + v, 0) };
  }, [statementKeys, bySection]);

  const netRevenue = useMemo(() => runningTo('DISCOUNTS').total, [runningTo]);

  // COMMON-SIZE BASE IS GROSS REVENUE, not net.
  //
  // The conventional common-size P&L runs on net revenue, but it makes the one
  // line most worth watching unreadable: discounts over net revenue is not the
  // discount rate, it is the discount rate divided by (1 - itself), and it
  // overstates every time. On CompanyA that is the difference between "we gave
  // away 8%" and "we gave away 9%". Gross revenue is the number discounts are
  // actually negotiated against, so every line here is a share of it — and the
  // discount line reads as the rate the commercial team agreed.
  const grossRevenue = useMemo(() => runningTo('GROSS_REVENUE').total, [runningTo]);

  // ---- Display rows: sections, their detail, and the subtotals between ------
  //
  // Empty sections vanish, and so does a subtotal with nothing new above it —
  // a services business should not read "Gross Profit" and "Net Revenue" on
  // consecutive lines with the same number in both.
  // Takes the fold state as an argument rather than reading it, so the export
  // can ask for the same statement with every section open. Folding is a
  // reading aid; a workbook that silently omitted the folded lines would be a
  // different document from the one on screen.
  const buildStatement = useCallback((foldedMap) => {
    const out = [];
    let sinceSubtotal = 0;
    sections.forEach((s) => {
      if (s.excluded) return;
      const sec = bySection.get(s.key);
      const has = sec && sec.lines.length > 0;
      if (has) {
        out.push({ kind: 'section', key: s.key, label: s.label, hint: s.hint,
                   values: sec.values, total: sec.total, count: sec.lines.length });
        if (!foldedMap[s.key]) {
          sec.lines.forEach((l) => out.push({ kind: 'line', ...l }));
        }
        sinceSubtotal += 1;
      }
      const sub = subtotals.find((t) => t.after === s.key);
      if (sub && (sinceSubtotal > 0 || sub.grand)) {
        const { values, total } = runningTo(sub.through);
        out.push({ kind: 'subtotal', key: sub.key, label: sub.label, values, total,
                   margin: sub.margin, grand: sub.grand });
        sinceSubtotal = 0;
      }
    });
    return out;
  }, [sections, bySection, subtotals, runningTo]);

  const display = useMemo(() => buildStatement(collapsed), [buildStatement, collapsed]);

  // ---- Headline figures ----------------------------------------------------
  // Only two, in the title bar. Gross revenue and every section total are
  // already rows in the statement below.
  const netProfit = useMemo(
    () => statementKeys.reduce((a, k) => a + (bySection.get(k)?.total || 0), 0),
    [statementKeys, bySection]
  );
  const nonPl = bySection.get('NON_PL');
  const unpostedTotal = useMemo(
    () => (data.unposted || []).reduce((a, u) => a + (Number(u.unposted_amount) || 0), 0),
    [data.unposted]
  );
  const marginPct = grossRevenue ? (netProfit / grossRevenue) * 100 : 0;

  // ---- Period drill-down: Full Year -> quarters -> months -------------------
  const {
    visibleCols, yearExpanded, expandedQuarters,
    toggleYear, toggleQuarter, expandAll, collapseAll,
  } = usePeriodDrilldown(PERIODS);

  const FIXED = grain === 'account' ? 2 : 1; // label (+ group, accounts only)

  const gcols = useMemo(() => {
    const base = [
      { id: 'label', title: grain === 'account' ? 'Account' : grain === 'module' ? 'Module' : 'Site',
        width: 320, grow: 1 },
      ...(grain === 'account' ? [{ id: 'group', title: 'Group', width: 130 }] : []),
    ];
    // Month width matches the OPEX grid so the two read as the same instrument
    // — twelve of these is the widest the statement ever gets.
    const dyn = visibleCols.map((c) => {
      if (c.kind === 'year') return { id: 'YEAR', title: `${yearExpanded ? '▾' : '▸'} Full Year`, width: 132 };
      if (c.kind === 'quarter') return { id: `Q${c.q}`, title: `${expandedQuarters[c.q] ? '▾' : '▸'} Q${c.q}`, width: 112 };
      return { id: `P${c.period}`, title: periodLabel(c.period, fyStartMonth), width: 88 };
    });
    return [
      ...base,
      ...dyn,
      // Only worth a column once Full Year has been broken up — until then the
      // Full Year column IS the total and two identical columns is clutter.
      ...(yearExpanded ? [{ id: 'total', title: 'Total', width: 128 }] : []),
      { id: 'pct', title: '% of Gross Rev', width: 104 },
    ];
  }, [grain, visibleCols, yearExpanded, expandedQuarters, fyStartMonth]);
  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const r = display[row];
    if (!r) return gcell.text('', { readonly: true });

    const theme = r.kind === 'section' ? T_SECTION
      : r.kind === 'subtotal' ? (r.grand ? (r.total >= 0 ? T_GRAND : T_GRAND_NEG) : T_SUBTOTAL)
        : undefined;

    if (id === 'label') {
      if (r.kind === 'section') {
        return gcell.text(`${collapsed[r.key] ? '▸' : '▾'} ${r.label.toUpperCase()}`, { readonly: true, theme });
      }
      if (r.kind === 'subtotal') return gcell.text(r.label.toUpperCase(), { readonly: true, theme });
      // Indent is two spaces: canvas cells have no padding to play with, and
      // the detail must read as subordinate to the section above it.
      return gcell.text(`  ${r.code && r.code !== r.label ? `${r.code}  ` : ''}${r.label}`, { readonly: true });
    }

    if (id === 'group') {
      if (r.kind === 'section') return gcell.text(`${r.count} line${r.count === 1 ? '' : 's'}`, { readonly: true, theme });
      if (r.kind === 'subtotal') return gcell.text('', { readonly: true, theme });
      return gcell.text(r.group || '', { readonly: true, theme: T_MUTED });
    }

    if (id === 'pct') {
      // Every line as a share of GROSS revenue — the ratio a reader checks
      // first and the only one that survives a change of scale. One base for
      // the whole statement, so any two lines can be compared directly.
      const pct = grossRevenue ? (r.total / grossRevenue) * 100 : 0;
      return gcell.text(fmtPct(pct), { readonly: true, theme: theme || T_MUTED });
    }

    if (id === 'total') {
      return gcell.number(r.total, { display: fmtR(r.total), readonly: true, theme: theme || T_YEAR });
    }

    const d = visibleCols[col - FIXED];
    if (!d) return gcell.text('', { readonly: true, theme });
    const v = d.kind === 'month' ? (r.values[d.period - 1] || 0) : sumOf(r, d.periods);
    return gcell.number(v, {
      display: fmtR(v),
      readonly: true,
      theme: theme || (d.kind === 'month' ? undefined : T_YEAR),
    });
  }, [colIds, display, visibleCols, collapsed, grossRevenue, FIXED]);

  // Clicking a section header folds its detail away. Same gesture as the month
  // headers, so the whole statement expands and collapses the same way.
  const displayRef = useRef([]);
  displayRef.current = display;
  // Only the heading itself, not its numbers — a reader clicking a section
  // total to copy it should not have the rows disappear underneath them.
  const onCellClicked = useCallback(([col, row]) => {
    if (col !== 0) return;
    const r = displayRef.current[row];
    if (r?.kind === 'section') setCollapsed((prev) => ({ ...prev, [r.key]: !prev[r.key] }));
  }, []);

  const onHeaderClicked = useCallback((colIndex) => {
    const id = colIds[colIndex];
    if (id === 'YEAR') toggleYear();
    else if (/^Q\d+$/.test(id)) toggleQuarter(Number(id.slice(1)));
  }, [colIds, toggleYear, toggleQuarter]);

  const allCollapsed = statementKeys.every((k) => collapsed[k] || !(bySection.get(k)?.lines.length));

  // ---- Everything wrong with the numbers, as one list ----------------------
  //
  // These used to sit above the statement, where three stacked banners pushed
  // the actual P&L off the first screen — the page shouting about its footnotes
  // before showing its subject. They live below it now, and a single counter by
  // the title carries the fact that they exist. Nothing is hidden: the counter
  // is red when a figure is missing from the statement, and jumps you there.
  const issues = useMemo(() => {
    const out = [];
    if (error) {
      out.push({ id: 'load', level: 'error', title: 'The statement could not be loaded', body: error });
    }
    if (unpostedTotal !== 0) {
      out.push({
        id: 'unposted', level: 'error',
        title: `${fmtR(unpostedTotal)} of budgeted value posts nowhere`,
        body: 'It has no GL account, so it is costed correctly and missing from every figure in the statement.',
        detail: (data.unposted || []).map((u) => `${u.module_code} ${fmtR(u.unposted_amount)}`).join(' · '),
        fix: (data.unposted || []).some((u) => u.module_code === 'MOD_6_PROFIT')
          ? 'Fix on the posting pages under each module’s Budget Setup. Unposted REVENUE is the worst version of this: the ledger loses the credit and the budget looks catastrophic rather than incomplete — Revenue › Budget Setup › Revenue Posting.'
          : 'Fix on the posting pages under each module’s Budget Setup.',
      });
    }
    if (nonPl && nonPl.total !== 0) {
      out.push({
        id: 'nonpl', level: 'warn',
        title: `${fmtR(Math.abs(nonPl.total))} is budgeted onto balance-sheet accounts`,
        body: 'An income statement cannot contain an asset, so this is excluded from every figure above. Usually the budget line points at the wrong account.',
        detail: nonPl.lines.slice(0, 8).map((l) => `${l.code} ${l.label}`).join(' · '),
        fix: 'Re-point the GL budget line, or change the account’s section in Master Data › GL Accounts.',
      });
    }
    if ((data.unclassified || []).length > 0) {
      const n = data.unclassified.length;
      out.push({
        id: 'unclassified', level: 'warn',
        title: `${n} account${n === 1 ? ' has' : 's have'} no P&L section set`,
        body: `${n === 1 ? 'It has' : 'They have'} been placed by account category — a guess, and it moves the subtotals.`,
        detail: data.unclassified.slice(0, 8)
          .map((u) => `${u.account_code} ${u.account_description} → ${u.fallback_section}`).join(' · ')
          + (n > 8 ? ` · +${n - 8} more` : ''),
        fix: 'Set them in Master Data › GL Accounts.',
      });
    }
    return out;
  }, [error, unpostedTotal, nonPl, data.unposted, data.unclassified]);

  const worstLevel = issues.some((i) => i.level === 'error') ? 'error'
    : issues.length ? 'warn' : null;

  const issuesRef = useRef(null);
  const goToIssues = () => issuesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // ---- Export --------------------------------------------------------------
  // Always the full twelve months and every detail line, whatever the screen is
  // currently showing — and the issues travel with it, because a workbook is
  // exactly where the caveats normally get lost.
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await exportPnl({
        versionLabel: selectedVersion,
        grain,
        statement: buildStatement({}),
        grossRevenue,
        fyStartMonth,
        issues,
      });
    } catch (e) {
      setError('Could not build the Excel file.');
    } finally {
      setExporting(false);
    }
  };

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to see the P&amp;L.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      {/* Two lines of chrome, then the statement. Everything advisory is below
          the grid; the counter beside the title is what says it is there. */}
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    onClick={() => navigate('/finance')}>
              <ArrowLeft className="w-3.5 h-3.5" /> Finance
            </button>
            <span className="text-slate-300 dark:text-slate-600">/</span>
            <h1 className="m-0 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 whitespace-nowrap">
              Profit &amp; Loss
            </h1>
            <span className="text-xs font-semibold text-slate-400">{selectedVersion}</span>
            {worstLevel && (
              <button
                onClick={goToIssues}
                title={issues.map((i) => i.title).join('\n')}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold transition-colors ${
                  worstLevel === 'error'
                    ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300'
                    : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300'}`}
              >
                <TriangleAlert className="w-3 h-3" />
                {issues.length} {issues.length === 1 ? 'issue' : 'issues'}
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {!loading && display.length > 0 && (
              <>
                <Figure label="Gross revenue" value={fmtR(grossRevenue)} />
                <Figure label="Net revenue" value={fmtR(netRevenue)}
                        sub={grossRevenue ? `${((netRevenue / grossRevenue) * 100).toFixed(1)}% of gross` : ''} />
                <Figure
                  label={netProfit >= 0 ? 'Profit' : 'Loss'}
                  value={fmtR(Math.abs(netProfit))}
                  sub={grossRevenue ? `${marginPct.toFixed(1)}% of gross` : ''}
                  tone={netProfit >= 0 ? 'good' : 'bad'}
                />
              </>
            )}
            <div className="flex items-center rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden">
              <span className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 dark:bg-slate-900">
                Detail by
              </span>
              {GRAINS.map(([g, label]) => (
                <button key={g} onClick={() => setGrain(g)}
                  className={`px-2.5 py-1.5 text-xs font-semibold transition-colors ${grain === g
                    ? 'bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                  {label}
                </button>
              ))}
            </div>
            <button
              className={btn}
              onClick={handleExport}
              disabled={exporting || loading || display.length === 0}
              title="Excel: the full statement, all twelve months and every detail line regardless of what is folded on screen, plus any outstanding issues on a second sheet."
            >
              <Download className="w-3.5 h-3.5" /> {exporting ? 'Building…' : 'Export'}
            </button>
            <button className={btn} onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ONE SCROLLBAR ON THIS PAGE, AND IT IS THE PAGE'S.
          The statement renders at its FULL height — every row, no scrollbar of
          its own — and the page scrolls past it to the issues and footnotes. A
          statement you scroll inside a box you also scroll is two scrollbars
          fighting over one document.
          The card must therefore carry NO height constraint: MantleDataGrid's
          fitHeight sizes the canvas to its rows, but its own `maxHeight: 100%`
          would cap it again the moment this box had a definite height. */}
      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col overflow-hidden">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : error ? (
            // The grid's own space, not a banner above it — an empty statement
            // with "nothing consolidated yet" underneath would be a lie when the
            // truth is that the request failed.
            <div className="py-10 px-6 text-center">
              <p className="m-0 text-sm font-semibold text-red-700 dark:text-red-300">The statement could not be loaded.</p>
              <p className="mt-1 mb-0 text-xs text-slate-500 dark:text-slate-400">{error}</p>
              <button className={`${btn} mt-4`} onClick={load}>
                <RefreshCw className="w-3.5 h-3.5" /> Try again
              </button>
            </div>
          ) : display.length === 0 ? (
            <p className="m-0 py-10 text-sm text-slate-400 text-center">
              Nothing consolidated yet for {selectedVersion}. Either no module has budgeted, or nothing has a posting rule.
            </p>
          ) : (
            <MantleDataGrid
              gridId={`finance-pnl-${grain}`}
              // fitHeight, with an UNCONSTRAINED parent: the canvas is exactly
              // as tall as the statement, so every line is on the page and the
              // grid never grows a vertical scrollbar of its own. The label
              // column grows to take whatever width the figures do not.
              fitHeight
              fitHeightPadding={6}
              columns={gcols}
              rowCount={display.length}
              getCellContent={getCellContent}
              onCellClicked={onCellClicked}
              onHeaderClicked={onHeaderClicked}
              freezeColumns={1}
              hint="Click Full Year → quarters → months to drill into the periods. Click a SECTION heading to fold its detail away. Read-only: every figure comes from the module that produced it."
              toolbar={
                <>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    {lines.length} line{lines.length === 1 ? '' : 's'} · brackets are costs
                  </span>
                  <button type="button" onClick={yearExpanded ? collapseAll : expandAll} className={btn}>
                    {yearExpanded ? 'Collapse periods' : 'Expand periods'}
                  </button>
                  <button
                    type="button"
                    className={btn}
                    onClick={() => setCollapsed(allCollapsed
                      ? {}
                      : Object.fromEntries(statementKeys.map((k) => [k, true])))}
                  >
                    {allCollapsed ? 'Show detail' : 'Sections only'}
                  </button>
                </>
              }
            />
          )}
        </div>

        {/* Below the fold, where footnotes belong. Nothing here is capped or
            scrolls on its own — the page scrolls, and the issue counter beside
            the title jumps you down here. */}
        <div ref={issuesRef} className="scroll-mt-2 space-y-3">
          {issues.length > 0 && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                <TriangleAlert className={`w-4 h-4 ${worstLevel === 'error' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                <h2 className="m-0 text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {issues.length} thing{issues.length === 1 ? '' : 's'} to fix before this statement is final
                </h2>
                <span className="text-[11px] text-slate-400">
                  red = a figure is missing from the statement · amber = a figure may be in the wrong place
                </span>
              </div>
              <ul className="m-0 p-0 list-none divide-y divide-slate-100 dark:divide-slate-700/60">
                {issues.map((i) => (
                  <li key={i.id} className={`px-4 py-2.5 border-l-4 ${
                    i.level === 'error' ? 'border-l-red-500 bg-red-50/40 dark:bg-red-500/5'
                      : 'border-l-amber-400 bg-amber-50/40 dark:bg-amber-500/5'}`}>
                    <p className="m-0 text-xs font-semibold text-slate-800 dark:text-slate-100">{i.title}</p>
                    <p className="m-0 mt-0.5 text-xs text-slate-600 dark:text-slate-300">{i.body}</p>
                    {i.detail && (
                      <p className="m-0 mt-1 text-[11px] font-mono text-slate-500 dark:text-slate-400 break-words">{i.detail}</p>
                    )}
                    {i.fix && <p className="m-0 mt-1 text-[11px] text-slate-500 dark:text-slate-400">{i.fix}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
            Sections come from each account&rsquo;s <code className="mx-0.5">pnl_section</code>, set in
            Master Data &rsaquo; GL Accounts. Income is positive and costs are in brackets, so every subtotal is the
            plain sum of the lines above it and the column adds up by hand. Every figure is derived from the module
            that produced it; nothing on this page is captured here.
          </p>

          {/* The one thing still missing from this statement, kept because it
              explains a figure on screen: EBITDA and EBIT read the same, and
              that is not a bug. */}
          <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
            <strong>Depreciation.</strong> Capital is still a mockup on dummy data, so the DEPRECIATION section exists
            but nothing posts to it &mdash; which is why EBITDA and EBIT currently read the same. Once
            <code className="mx-1">bud_assets</code> carries a useful life and a capitalisation period it unions into
            <code className="mx-1">vw_bud_gl_consolidated</code> like every other source and the section fills itself in.
          </p>
        </div>
      </div>
    </div>
  );
}

// Single-line headline figure. Deliberately not a stat card: the statement
// below already carries Net Revenue, Gross Profit and Net Profit as subtotal
// rows, so a stack of tiles restating them is a second copy of the answer that
// costs the statement a chunk of the first screen.
const Figure = ({ label, value, sub, tone }) => {
  const cls = tone === 'bad'
    ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300'
    : tone === 'good'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300'
      : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200';
  return (
    <div className={`inline-flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-md border ${cls}`}>
      <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">{label}</span>
      <span className="text-sm font-bold tabular-nums">{value}</span>
      {sub && <span className="text-[10px] opacity-70">{sub}</span>}
    </div>
  );
};
